import React, { useState, useMemo } from 'react';
import { Body, SectionTitle } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import ClickableStack from 'talent_matching/components/shared/clickable_stack';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import ArrowUpIcon from '@grnhse/seedling/lib/birch/icons/arrow_up';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { useApplicationHistory } from 'talent_matching/api';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  TalentRediscoveryFilters,
  SearchFormData,
} from 'talent_search/shared/types';
import queryParams from 'talent_search/lib/query_params';
import { advancedFilterParams } from 'talent_matching/hooks/use_people';
import { useUserContext } from 'talent_matching/contexts/user_context';
import RediscoveryApplicationCard from './rediscovery_application_card';
import RediscoveryCurrentApplicationCard from './rediscovery_current_application_card';
import type {
  RediscoveryApplicationHistory as RediscoveryApplicationHistoryType,
  RediscoveryApplicationHistoryItem,
  Document,
} from 'talent_matching/types';

const t = withLocalePrefix(
  'talent_matching.panel.rediscovery_application_history'
);

type Props = {
  historyUrl: string | undefined;
  activeDocument?: Document | null;
  onOpenDocument?: (document: Document) => void;
  canDownloadCandidateData?: boolean;
};

type CollapsibleSectionProps = {
  id: string;
  title: string;
  applications: RediscoveryApplicationHistoryItem[];
  defaultOpen?: boolean;
  activeDocument?: Document | null;
  onOpenDocument?: (document: Document) => void;
  canDownloadCandidateData?: boolean;
};

const CollapsibleSection = ({
  id,
  title,
  applications,
  defaultOpen = true,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData,
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (applications.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <ClickableStack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Body medium>{title}</Body>
        {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
      </ClickableStack>
      <Collapsible id={id} isOpen={isOpen}>
        <Stack spacing={2}>
          {applications.map((app) => (
            <RediscoveryApplicationCard
              key={app.id}
              application={app}
              activeDocument={activeDocument}
              onOpenDocument={onOpenDocument}
              canDownloadCandidateData={canDownloadCandidateData}
            />
          ))}
        </Stack>
      </Collapsible>
    </Stack>
  );
};

const useRediscoverySearchParams = () => {
  const { control } = useFormContext<TalentRediscoveryFilters>();
  const formValues = useWatch({ control }) as TalentRediscoveryFilters;
  const { hiringPlanId } = useUserContext();

  return useMemo(() => {
    const transformedParams = queryParams(
      formValues as unknown as SearchFormData
    );

    return {
      ...transformedParams,
      ...advancedFilterParams(formValues.advancedFilters || []),
      current_hiring_plan_id: hiringPlanId,
    };
  }, [formValues, hiringPlanId]);
};

const RediscoveryApplicationHistory = ({
  historyUrl,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData = false,
}: Props) => {
  const searchParams = useRediscoverySearchParams();
  const { data, isLoading, isFetching, error, refetch } = useApplicationHistory(
    historyUrl as string,
    searchParams
  );

  if (isLoading || isFetching) {
    return <SkeletonLoader columnsPerRow={1} rows={5} />;
  }

  if (error) {
    return (
      <Stack>
        <Body>{t('error')}</Body>
        <SecondaryButton onClick={() => refetch()} size="md">
          {t('retry')}
        </SecondaryButton>
      </Stack>
    );
  }

  const applicationHistory: RediscoveryApplicationHistoryType = data;

  return (
    <Stack spacing={2} sx={{ paddingTop: 1 }}>
      <RediscoveryCurrentApplicationCard
        application={applicationHistory.currentApplication}
        onSummaryGenerated={refetch}
      />

      <SectionTitle large>{t('title')}</SectionTitle>

      <CollapsibleSection
        id="active-applications"
        title={t('active_applications', {
          count: applicationHistory.activeApplications.length,
        })}
        applications={applicationHistory.activeApplications}
        activeDocument={activeDocument}
        onOpenDocument={onOpenDocument}
        canDownloadCandidateData={canDownloadCandidateData}
      />

      <CollapsibleSection
        id="previous-applications"
        title={t('previous_applications', {
          count: applicationHistory.previousApplications.length,
        })}
        applications={applicationHistory.previousApplications}
        activeDocument={activeDocument}
        onOpenDocument={onOpenDocument}
        canDownloadCandidateData={canDownloadCandidateData}
      />
    </Stack>
  );
};

export default RediscoveryApplicationHistory;
